import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isFreshRequest, requestQueue, type TripDto } from "@tc/shared";
import { Button, Card, Icon, StatusBadge, TRIP_STATUS_BADGE } from "@/components/ui";
import { AssignOwnerModal } from "@/features/ops/AssignOwnerModal";
import { useOpsWorklist } from "@/features/ops/useOpsWorklist";
import { STATUS_KEY } from "@/features/trips/status";
import { TransportIcons } from "@/features/trips/TripCard";
import { Can } from "@/lib/auth/authz";
import { useClubLookup } from "@/lib/clubs/clubs";
import { useFormatters } from "@/lib/i18n/format";

/**
 * Back-office requests inbox (TW-3440): incoming club enquiries awaiting a proposal, with the
 * full enquiry detail TC needs to triage — destination, dates, pax, transport, board and the
 * club's free-text notes. Scoped by the topbar club switcher; a fresh (unassigned) request is
 * marked, and an operator can assign an owner or open the enquiry to begin a proposal.
 */
export function RequestsPage() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const clubs = useClubLookup();
  const { trips, owners, loading, error, reload } = useOpsWorklist();
  const [assigning, setAssigning] = useState<TripDto | null>(null);

  const queue = requestQueue(trips);

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t("worklist.requestsTitle")}</h1>
        <div className="sub">{t("worklist.requestsSub")}</div>
      </div>

      {loading ? (
        <div className="empty">{t("common.loading")}</div>
      ) : error ? (
        <div className="empty">{t("ops.loadError")}</div>
      ) : queue.length === 0 ? (
        <Card>
          <div className="empty">
            <div className="empty-ic">
              <Icon name="bell" size={24} />
            </div>
            {t("ops.noRequests")}
          </div>
        </Card>
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          {queue.map((trip) => {
            const fresh = isFreshRequest(trip);
            const dest = [trip.destinationCity, trip.destinationCountry ? fmt.country(trip.destinationCountry) : ""].filter(Boolean).join(", ");
            return (
              <Card key={trip.id} className={`queue-item${fresh ? " fresh" : ""}`}>
                <div className="queue-body">
                  <div className="queue-top">
                    <span className="t-id mono">{trip.reference}</span>
                    <StatusBadge status={TRIP_STATUS_BADGE[trip.status]} label={t(STATUS_KEY[trip.status])} />
                    {fresh && <span className="choice-badge choice-1">{t("ops.newLabel")}</span>}
                  </div>
                  <div className="queue-match" style={{ fontSize: 16 }}>
                    {trip.matchOpponent ?? trip.sport ?? trip.reference}
                  </div>
                  <div className="queue-meta">
                    <span className="mi">
                      <span className="club-dot" style={{ background: clubs.color(trip.clubId) }} />
                      {clubs.name(trip.clubId) ?? "—"}
                    </span>
                    {dest && (
                      <span className="mi">
                        <Icon name="building" size={13} />
                        {dest}
                      </span>
                    )}
                    {trip.travelStartDate && (
                      <span className="mi">
                        <Icon name="calendar" size={13} />
                        {fmt.date(trip.travelStartDate)}
                        {trip.travelEndDate && ` – ${fmt.date(trip.travelEndDate)}`}
                      </span>
                    )}
                    {trip.paxCount != null && (
                      <span className="mi">
                        <Icon name="roster" size={13} />
                        {trip.paxCount} {t("units.pax")}
                      </span>
                    )}
                    <span className="mi">
                      <TransportIcons modes={trip.transportModes} />
                    </span>
                  </div>
                  {trip.notes && (
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--text-muted)",
                        marginTop: 8,
                        fontStyle: "italic",
                      }}
                    >
                      “{trip.notes}”
                    </div>
                  )}
                </div>
                <div className="queue-side">
                  <div style={{ textAlign: "right" }}>
                    <div className="faint" style={{ fontSize: 11 }}>
                      {t("worklist.colReceived")}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{fmt.relative(trip.createdAt)}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Can permission="trip.write">
                      <Button variant="soft" size="sm" icon="user" onClick={() => setAssigning(trip)}>
                        {t("worklist.assign")}
                      </Button>
                    </Can>
                    <Button
                      variant="primary"
                      size="sm"
                      iconRight="arrowR"
                      onClick={() => navigate(`/trips/${trip.id}`)}
                    >
                      {t("ops.buildProposal")}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {assigning && (
        <AssignOwnerModal
          trip={assigning}
          owners={owners}
          onClose={() => setAssigning(null)}
          onSaved={async () => {
            setAssigning(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}
